import React, { useState, useEffect } from 'react';
import { getMentorRequests, updateRequestStatus } from '../services/mentorService';
import { useAuth } from '../context/AuthContext';
import { useAlertContext } from '../context/AlertContext';
import { FaUserGraduate, FaCheck, FaTimes, FaSpinner, FaClock } from 'react-icons/fa';

const MentorDashboard = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { showAlert } = useAlertContext();

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const data = await getMentorRequests();

      if (data.success) {
        setRequests(data.requests);
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
      // Only show error if it's not a 404 (no profile found yet)
      if (error.response?.status !== 404) {
          showAlert({
            type: 'error',
            title: 'Error',
            message: 'Failed to fetch your requests'
          });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (requestId, newStatus) => {
    try {
      await updateRequestStatus(requestId, newStatus);
      
      showAlert({
        type: 'success',
        title: 'Success',
        message: `Request ${newStatus}`
      });
      fetchRequests(); // Refresh data
    } catch (error) {
      console.error('Error updating status:', error);
      showAlert({
        type: 'error',
        title: 'Error',
        message: 'Failed to update request status'
      });
    }
  };

  if (loading) {
     return (
        <div className="flex justify-center items-center min-h-screen">
          <FaSpinner className="animate-spin text-4xl text-blue-600" />
        </div>
      );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6 md:p-12">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
            <h1 className="heading-primary text-gray-900">Mentor Dashboard</h1>
            <div className="bg-white px-4 py-2 rounded-lg shadow-sm">
                <span className="text-gray-500 text-sm">Welcome back, </span>
                <span className="font-semibold text-primary-600">{user?.name}</span>
            </div>
        </div>

        {/* Stats Summary could go here */}

        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="heading-tertiary text-gray-900">Student Requests</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-gray-50 text-gray-600 font-medium">
                <tr>
                  <th className="p-4">Student</th>
                  <th className="p-4">Topic</th>
                  <th className="p-4">Requested Time</th>
                  <th className="p-4">Message</th>
                  <th className="p-4">Status</th>
                  <th className="p-4">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {requests.map((req) => (
                  <tr key={req._id} className="hover:bg-gray-50 transition-colors">
                    <td className="p-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                            <FaUserGraduate />
                        </div>
                        <div>
                            <div className="font-medium text-gray-900">{req.student?.username || 'Unknown'}</div>
                            <div className="text-xs text-gray-500">{req.student?.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 font-medium text-primary-600">{req.topic}</td>
                    <td className="p-4">
                      <div className="flex items-center text-sm text-gray-600">
                        <FaClock className="mr-2 text-gray-400" />
                        <span>{req.meetingDay}, {req.meetingTime}</span>
                      </div>
                    </td>
                    <td className="p-4 max-w-xs truncate text-gray-600" title={req.message}>
                      {req.message}
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold
                        ${req.status === 'pending' ? 'bg-orange-100 text-orange-700' : 
                          req.status === 'accepted' ? 'bg-green-100 text-green-700' :
                          req.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}
                      `}>
                        {req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                      </span>
                    </td>
                    <td className="p-4">
                      {req.status === 'pending' && (
                        <div className="flex space-x-2">
                          <button 
                            onClick={() => handleStatusUpdate(req._id, 'accepted')}
                            className="flex items-center px-3 py-1.5 bg-green-100 text-green-700 rounded hover:bg-green-200 text-sm font-medium transition-colors"
                          >
                            <FaCheck className="mr-1.5" size={12} /> Accept
                          </button>
                          <button 
                            onClick={() => handleStatusUpdate(req._id, 'rejected')}
                            className="flex items-center px-3 py-1.5 bg-red-100 text-red-700 rounded hover:bg-red-200 text-sm font-medium transition-colors"
                          >
                            <FaTimes className="mr-1.5" size={12} /> Reject
                          </button>
                        </div>
                      )}
                      {req.status === 'accepted' && (
                          <div className="text-xs text-green-600 font-medium">Scheduled</div>
                      )}
                    </td>
                  </tr>
                ))}
                {requests.length === 0 && (
                  <tr>
                    <td colSpan="6" className="p-12 text-center">
                        <div className="flex flex-col items-center justify-center text-gray-400">
                            <FaUserGraduate className="text-4xl mb-3" />
                            <p className="text-lg font-medium text-gray-500">No requests yet</p>
                            <p className="text-sm">When students request mentorship, they will appear here.</p>
                        </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MentorDashboard;
